import React, { useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Trash2, Plus } from "lucide-react";
import { WEEKDAY_LABELS, resolveCustomPreset } from "./DateRangePresets";

const TYPES = [
  { v: "last_days", label: "Ultimi N giorni" },
  { v: "weekdays", label: "Giorni della settimana corrente" },
  { v: "fixed", label: "Intervallo fisso" },
];

const describe = (p) => {
  if (p.type === "last_days") return `Ultimi ${p.days} giorni`;
  if (p.type === "weekdays") return (p.weekdays || []).map((d) => WEEKDAY_LABELS[d]).join("+") + " (settimana corrente)";
  return `${p.from} → ${p.to}`;
};

const EMPTY = { name: "", type: "last_days", days: 14, weekdays: [5, 6], from: "", to: "" };

// Admin editor for custom date presets stored in settings.date_presets
export default function DatePresetsEditor({ presets = [], onChange }) {
  const [draft, setDraft] = useState(EMPTY);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const valid = draft.name.trim().length > 0 && (
    (draft.type === "last_days" && parseInt(draft.days, 10) >= 1) ||
    (draft.type === "weekdays" && draft.weekdays.length > 0) ||
    (draft.type === "fixed" && draft.from && draft.to && draft.to >= draft.from)
  );

  const add = () => {
    if (!valid) return;
    const p = { id: Date.now().toString(36), name: draft.name.trim(), type: draft.type };
    if (p.type === "last_days") p.days = parseInt(draft.days, 10);
    if (p.type === "weekdays") p.weekdays = [...draft.weekdays].sort((a, b) => a - b);
    if (p.type === "fixed") { p.from = draft.from; p.to = draft.to; }
    onChange([...presets, p]);
    setDraft(EMPTY);
  };

  const preview = resolveCustomPreset(draft);

  return (
    <div className="border-t border-zinc-800 pt-4" data-testid="settings-date-presets-panel">
      <Label className="text-zinc-300 mb-2 flex items-center gap-1 text-xs uppercase tracking-widest">
        📅 Preset date personalizzati
      </Label>
      <p className="text-[11px] text-zinc-500 mb-3">
        Appaiono come chip ★ accanto a Oggi/Settimana/Mese in Ordini, Prenotazioni, Statistiche e Movimenti.
      </p>

      {presets.length > 0 && (
        <ul className="space-y-1.5 mb-3" data-testid="date-presets-list">
          {presets.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2 bg-zinc-950/60 border border-zinc-800 rounded-lg px-3 py-1.5" data-testid={`date-preset-row-${p.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-zinc-200 truncate">★ {p.name}</div>
                <div className="text-[10px] text-zinc-500">{describe(p)}</div>
              </div>
              <button
                type="button"
                data-testid={`date-preset-remove-${p.id}`}
                onClick={() => onChange(presets.filter((x) => x.id !== p.id))}
                className="text-red-400 hover:text-red-300 shrink-0"
                title="Rimuovi"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-3 space-y-2" data-testid="date-preset-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input
            data-testid="date-preset-name"
            value={draft.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder='Nome (es. "Mio weekend lavorativo")'
            className="bg-zinc-950 border-zinc-800 h-9 text-sm"
          />
          <select
            data-testid="date-preset-type"
            value={draft.type}
            onChange={(e) => set("type", e.target.value)}
            className="h-9 rounded-md bg-zinc-950 border border-zinc-800 text-sm text-zinc-200 px-2"
          >
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </div>

        {draft.type === "last_days" && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-zinc-500">Ultimi</span>
            <Input
              type="number" min={1} max={730}
              data-testid="date-preset-days"
              value={draft.days}
              onChange={(e) => set("days", e.target.value)}
              className="bg-zinc-950 border-zinc-800 h-9 w-24 text-sm"
            />
            <span className="text-[11px] text-zinc-500">giorni (incluso oggi)</span>
          </div>
        )}

        {draft.type === "weekdays" && (
          <div className="flex flex-wrap gap-1" data-testid="date-preset-weekdays">
            {WEEKDAY_LABELS.map((l, i) => {
              const on = draft.weekdays.includes(i);
              return (
                <button
                  key={l} type="button"
                  data-testid={`date-preset-weekday-${i}`}
                  onClick={() => set("weekdays", on ? draft.weekdays.filter((x) => x !== i) : [...draft.weekdays, i])}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition ${on ? "bg-indigo-500/20 text-indigo-200 border-indigo-500/60" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}
                >
                  {l}
                </button>
              );
            })}
            <span className="text-[10px] text-zinc-500 self-center ml-1">L'intervallo va dal primo all'ultimo giorno scelto della settimana in corso.</span>
          </div>
        )}

        {draft.type === "fixed" && (
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" data-testid="date-preset-from" value={draft.from} onChange={(e) => set("from", e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 text-sm" />
            <Input type="date" data-testid="date-preset-to" value={draft.to} min={draft.from || undefined} onChange={(e) => set("to", e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 text-sm" />
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-zinc-500" data-testid="date-preset-preview">
            {preview.from && preview.to ? `Oggi risolve in: ${preview.from} → ${preview.to}` : "Completa la regola"}
          </span>
          <Button size="sm" onClick={add} disabled={!valid} data-testid="date-preset-add" className="h-8 bg-indigo-600 hover:bg-indigo-500">
            <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi
          </Button>
        </div>
      </div>
      <div className="text-[10px] text-zinc-500 mt-2">Ricorda di premere Salva in alto per rendere i preset visibili a tutto il team.</div>
    </div>
  );
}
